'use client'

import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import { cn } from '@/lib/utils'

// Module-level so the plugin arrays aren't recreated on every render.
const remarkPlugins = [remarkGfm]
// react-markdown does not render raw HTML by default (no rehype-raw), so user content can't inject
// markup. rehype-sanitize is defense-in-depth in case a raw-HTML plugin is ever added.
const rehypePlugins = [rehypeSanitize]

// Inherit the surrounding text colour (currentColor) so the same content reads correctly on both
// the light and the primary-coloured chat bubbles.
const components: Components = {
  p: ({ children }) => <p className="break-words [&:not(:first-child)]:mt-2">{children}</p>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="break-all underline underline-offset-2">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="list-disc space-y-0.5 pl-5 [&:not(:first-child)]:mt-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-0.5 pl-5 [&:not(:first-child)]:mt-2">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through">{children}</del>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-current/30 pl-3 opacity-90 [&:not(:first-child)]:mt-2">{children}</blockquote>,
  h1: ({ children }) => <h3 className="text-[1.05em] font-semibold [&:not(:first-child)]:mt-2">{children}</h3>,
  h2: ({ children }) => <h3 className="text-[1.05em] font-semibold [&:not(:first-child)]:mt-2">{children}</h3>,
  h3: ({ children }) => <h3 className="font-semibold [&:not(:first-child)]:mt-2">{children}</h3>,
  hr: () => <hr className="my-2 border-current/20" />,
  code: ({ children }) => (
    <code className="rounded bg-black/10 px-1 py-0.5 font-mono text-[0.85em] [pre_&]:bg-transparent [pre_&]:p-0">{children}</code>
  ),
  pre: ({ children }) => <pre className="overflow-x-auto rounded bg-black/10 p-2 text-[0.85em] [&:not(:first-child)]:mt-2">{children}</pre>,
  table: ({ children }) => (
    <div className="overflow-x-auto [&:not(:first-child)]:mt-2">
      <table className="w-full border-collapse text-[0.9em]">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-current/20 px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-current/20 px-2 py-1">{children}</td>,
}

type MarkdownTextProps = {
  children: string
  className?: string
}

export function MarkdownText({ children, className }: MarkdownTextProps) {
  return (
    <div className={cn('break-words', className)}>
      <ReactMarkdown remarkPlugins={remarkPlugins} rehypePlugins={rehypePlugins} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  )
}
